import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Sparkles, Loader2, AlertCircle, BadgeCheck, RefreshCw, ListChecks, Layers, Lightbulb } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader, Loading, ErrorState } from "@/components/States";
import { SelectionForm } from "@/components/SelectionForm";
import { usePlan } from "@/lib/plan";
import { UpgradeNote } from "@/components/UpgradeNote";
import { api, fetchSubjects, formatError, runJob } from "@/lib/api";

const Section = ({ title, children, testId }) => (
  <section data-testid={testId} className="border-t border-line pt-6">
    <h3 className="mb-3 text-xs font-semibold uppercase tracking-[0.16em] text-aqua">{title}</h3>{children}
  </section>
);

const CONF = { high: "bg-emerald-50 text-emerald-800 border-emerald-200", medium: "bg-amber-50 text-amber-800 border-amber-200", low: "bg-red-50 text-red-800 border-red-200" };

function Explanation({ data }) {
  const c = data.content;
  const [open, setOpen] = useState({});
  return (
    <article data-testid="explanation-output" className="mt-8 flex flex-col gap-6 rounded-2xl border border-line bg-surface p-6 sm:p-10 fade-up">
      <div className="flex flex-wrap items-center gap-2">
        <span data-testid="explanation-status" className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium ${data.status === "approved" ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-line bg-canvas-2 text-ink-2"}`}>
          {data.status === "approved" ? <><BadgeCheck className="h-3.5 w-3.5" />Reviewed by IDDCR</> : <><Sparkles className="h-3.5 w-3.5" />AI-generated · pending expert review</>}
        </span>
        <span data-testid="explanation-confidence" className={`rounded-full border px-3 py-1 text-xs font-medium capitalize ${CONF[c.confidence]}`}>AI confidence: {c.confidence}</span>
        {data.cached && <span className="text-xs text-ink-3">Served from saved content</span>}
      </div>
      <h2 className="text-2xl font-extrabold text-ink sm:text-3xl">{data.topic}</h2>
      <div className="space-y-3 text-[15px] leading-relaxed text-ink-2">{c.overview.split("\n").filter(Boolean).map((p, i) => <p key={i}>{p}</p>)}</div>
      {c.definitions.length > 0 && (
        <Section title="Key definitions" testId="explanation-definitions">
          <dl className="grid gap-3 sm:grid-cols-2">
            {c.definitions.map((d, i) => <div key={i} className="rounded-lg bg-canvas-2 p-4"><dt className="font-semibold text-ink">{d.term}</dt><dd className="mt-1 text-sm text-ink-2">{d.definition}</dd></div>)}
          </dl>
        </Section>
      )}
      {c.examples.length > 0 && (
        <Section title="Real-world examples"><ul className="list-disc space-y-2 pl-5 text-sm text-ink-2">{c.examples.map((e, i) => <li key={i}>{e}</li>)}</ul></Section>
      )}
      <Section title="Summary"><p className="text-sm leading-relaxed text-ink-2">{c.summary}</p></Section>
      <Section title="Key takeaways" testId="explanation-takeaways">
        <ul className="space-y-2">{c.key_takeaways.map((t, i) => <li key={i} className="flex gap-3 text-sm text-ink-2"><span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-aqua-solid" />{t}</li>)}</ul>
      </Section>
      {c.practice_questions.length > 0 && (
        <Section title="Practice questions" testId="explanation-practice">
          <div className="flex flex-col gap-2">
            {c.practice_questions.map((p, i) => (
              <div key={i} className="rounded-lg border border-line p-4">
                <p className="text-sm font-semibold text-ink">{p.question}</p>
                {open[i] ? <p className="mt-2 text-sm text-ink-2">{p.answer}</p> : <button data-testid={`practice-reveal-${i}`} onClick={() => setOpen({ ...open, [i]: true })} className="mt-2 text-xs font-semibold text-aqua hover:underline">Reveal answer</button>}
              </div>
            ))}
          </div>
        </Section>
      )}
      {c.revision_suggestions.length > 0 && (
        <Section title="Revision suggestions"><ul className="space-y-2">{c.revision_suggestions.map((r, i) => <li key={i} className="flex gap-2 text-sm text-ink-2"><Lightbulb className="h-4 w-4 shrink-0 text-amber-500" />{r}</li>)}</ul></Section>
      )}
      {c.uncertainty_note && (
        <div data-testid="explanation-uncertainty" className="flex gap-3 rounded-xl border border-amber-200 bg-amber-50/70 p-4 text-sm text-amber-900">
          <AlertCircle className="h-5 w-5 shrink-0" /><p><b>Verify before relying on this: </b>{c.uncertainty_note}</p>
        </div>
      )}
    </article>
  );
}

export default function Learn() {
  const { plan, limits, reload } = usePlan();
  const quotaUsed = limits && limits.explanations != null && plan.usage.explanations >= limits.explanations;
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [subjects, setSubjects] = useState(null);
  const [sel, setSel] = useState({ subject_id: params.get("subject") || "", topic: params.get("topic") || "", difficulty: params.get("difficulty") || "beginner" });
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => { fetchSubjects().then(setSubjects).catch((e) => setErr(formatError(e))); }, []);

  const generate = async (regenerate = false) => {
    setBusy(true); setErr("");
    try { setData(await runJob("/learn/explain", { ...sel, regenerate })); } catch (e) { setErr(formatError(e)); }
    reload();
    setBusy(false);
  };
  if (!subjects) return err ? <ErrorState message={err} /> : <Loading />;
  const q = `subject=${sel.subject_id}&topic=${encodeURIComponent(sel.topic)}&difficulty=${sel.difficulty}`;

  return (
    <div>
      <PageHeader eyebrow="AI Learning Assistant" title="Understand any topic" text="Choose a subject, topic and level to get a structured explanation with definitions, examples and key takeaways." />
      <div className="rounded-2xl border border-line bg-surface p-6">
        {quotaUsed && <div className="mb-5"><UpgradeNote compact testId="explain-quota-used" text={`You've used today's ${limits?.explanations} AI explanations on the ${plan.plan_name} plan. Come back tomorrow or upgrade for more.`} /></div>}
        <SelectionForm limits={limits} subjects={subjects} value={sel} onChange={setSel} />
        <div className="mt-5 flex flex-wrap gap-2">
          <Button data-testid="explain-generate-btn" disabled={!sel.subject_id || !sel.topic || busy || quotaUsed} onClick={() => generate(false)} className="rounded-full">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}Explain this topic
          </Button>
          {data && <>
            <Button data-testid="explain-regenerate-btn" variant="outline" disabled={busy} onClick={() => generate(true)} className="rounded-full"><RefreshCw className="mr-2 h-4 w-4" />Regenerate</Button>
            <Button data-testid="explain-quiz-btn" variant="outline" onClick={() => nav(`/quiz?${q}`)} className="rounded-full"><ListChecks className="mr-2 h-4 w-4" />Quiz me on this</Button>
            <Button data-testid="explain-flashcards-btn" variant="outline" onClick={() => nav(`/flashcards?${q}`)} className="rounded-full"><Layers className="mr-2 h-4 w-4" />Make flashcards</Button>
          </>}
        </div>
      </div>
      {err && <div className="mt-6"><ErrorState message={err} /></div>}
      {busy && <Loading label="Generating a structured explanation (about 15–30 seconds)..." />}
      {!busy && data && <Explanation data={data} />}
    </div>
  );
}
